import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconSuccessSolid from '@apollo/icons/default/IconSuccessSolid.svg';
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
} from '@apollo/orbit';
import React, { useEffect, useState } from 'react';

import { useSchemaFromEndpointIntrospection } from 'src/app/graph/explorerPage/hooks/useExplorerState/useSchema/useSchemaFromEndpointIntrospection/useSchemaFromEndpointIntrospection';
import { ClickableText } from 'src/components/common/clickableText/ClickableText';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { DocsRouter } from 'src/lib/routers';

import { CommonCloudDedicatedProps, CommonStepProps } from '../shared';

import { useComposedUrl } from './awsArnHelpers';
import { UploadSchema } from './UploadSchema';

export const AwsArnSection = ({
  availableUnregisteredResources,
  values,
  touched,
  setFieldValue,
  isDedicatedPlan,
  validateField,
  schemaUploaded,
  setSchemaUploaded,
  setIsUsingLocalIntrospection,
  selectedPrivateDomainName,
  setSelectedPrivateDomainName,
}: Pick<
  CommonStepProps,
  | 'errors'
  | 'touched'
  | 'values'
  | 'setFieldValue'
  | 'handleBlur'
  | 'validateField'
> &
  Pick<
    CommonCloudDedicatedProps,
    | 'availableUnregisteredResources'
    | 'selectedPrivateDomainName'
    | 'setSelectedPrivateDomainName'
  > & {
    schemaUploaded: boolean;
    setSchemaUploaded: React.Dispatch<React.SetStateAction<boolean>>;
    setIsUsingLocalIntrospection: React.Dispatch<React.SetStateAction<boolean>>;
    isDedicatedPlan: boolean;
  }) => {
  const [subgraphUrlPath, setSubgraphUrlPath] = useState('/api/graphql');

  const { introspectionFailureType } = useSchemaFromEndpointIntrospection({
    includeCookies: 'cycle-on-unreachable',
    uri: values.unregisteredEndpointUrl,
    skipPolling: true,
    pollForSubgraphSdlFirst: false,
    updateOnConfigChange: true,
  });
  const endpointURLTooltipText = isDedicatedPlan ? (
    <span>
      Your endpoint provides access for Apollo GraphOS to interact with your
      graph. For more info, check out our doc on{' '}
      <ClickableText
        as="a"
        className="ml-1 inline text-link"
        target="_blank"
        rel="noopener noreferrer"
        href={DocsRouter.path('ApiKeys')}
      >
        API Keys
      </ClickableText>
      .
    </span>
  ) : (
    "Your supergraph's router will use this URL to query your API. We'll also use the URL once now to introspect your API and fetch its schema. If your API has introspection disabled, you can upload your schema under Advanced options."
  );

  // Filter the available resources by the selected cloud region.
  const filteredResources = availableUnregisteredResources.filter(
    (resource) =>
      !values.cloudRegion || resource.region.code === values.cloudRegion,
  );

  const { composedUrl, setInputUrl } = useComposedUrl({
    values,
    availableUnregisteredResources,
    selectedPrivateDomainName,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newInputUrl = e.target.value;
    setInputUrl(newInputUrl);
    setSubgraphUrlPath(e.target.value);
  };

  // This useEffect is necessary because we programmatically
  // set the unregisteredSubgraphName in the onChange of the endpoint url field
  // so the user can click 'next' without focusing & unfocusing the api name field
  useEffect(() => {
    if (
      values.unregisteredSubgraphName.length > 0 &&
      !touched.unregisteredSubgraphName
    ) {
      validateField('unregisteredSubgraphName');
    }
  }, [
    touched.unregisteredSubgraphName,
    validateField,
    values.unregisteredSubgraphName.length,
  ]);

  useEffect(() => {
    if (composedUrl !== values.unregisteredEndpointUrl) {
      setFieldValue('unregisteredEndpointUrl', composedUrl, false);
    }
  }, [
    composedUrl,
    setFieldValue,
    values.sdl,
    values.unregisteredEndpointUrl,
    values.unregisteredSubgraphName,
  ]);

  return (
    <div className="mt-4 flex flex-col gap-8">
      {selectedPrivateDomainName && (
        <div>
          <div className="font-semibold">Private service domain name</div>
          <div className="my-2 mb-4 flex min-h-12 items-center rounded-md border  border-silver bg-silver-lighter px-4 py-2 text-base text-secondary">
            {selectedPrivateDomainName}
          </div>
        </div>
      )}
      {values.unregisteredEndpointUrl ? (
        <FormControl>
          <FormLabel>
            Add a url path{' '}
            <Tooltip label={endpointURLTooltipText} interactive>
              <IconInfo
                tabIndex={0}
                data-testid="focusable-info-endpoint-url"
              />
            </Tooltip>
          </FormLabel>
          <FormHelperText>Add the unique url path.</FormHelperText>
          <Input
            type="text"
            name="subgraphUrlPath"
            placeholder="/api/graphql"
            value={subgraphUrlPath}
            onChange={handleInputChange}
          />
        </FormControl>
      ) : (
        <>
          {availableUnregisteredResources.length > 0 && (
            <div className="flex flex-row items-end">
              <FormControl className="flex-auto">
                <FormLabel>Host</FormLabel>
                <FormHelperText>
                  Please select one of the hosts from this list to add your
                  first service to your <b className="ml-1">GraphOS Router</b>.
                </FormHelperText>
                <Select
                  id="unregisteredPrivateEndpointUrl"
                  name="unregisteredPrivateEndpointUrl"
                  onChange={(e) => {
                    const { value } = e.target;
                    setSelectedPrivateDomainName(value);
                  }}
                  value={selectedPrivateDomainName ?? ''}
                >
                  {filteredResources.map((resource) => (
                    <option
                      key={resource.domainUrl}
                      value={resource.domainUrl || 'no url'}
                    >
                      {resource.domainUrl}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl className="basis-1/4 pl-2">
                <FormLabel>Path</FormLabel>
                <FormHelperText>Add the unique url path.</FormHelperText>
                <Input
                  type="text"
                  name="subgraphUrlPath"
                  placeholder="/api/graphql"
                  value={subgraphUrlPath}
                  onChange={handleInputChange}
                />
              </FormControl>
            </div>
          )}
        </>
      )}
      {composedUrl && (
        <FormControl isReadOnly>
          <FormLabel>
            Full GraphQL service url
            <Tooltip
              className="w-72"
              label={endpointURLTooltipText}
              interactive
            >
              <IconInfo
                data-testid="focusable-info-endpoint-url text-secondary"
                tabIndex={0}
              />
            </Tooltip>
          </FormLabel>
          <FormHelperText>
            This is the url path the GraphOS Router will use.
          </FormHelperText>
          <div className="flex h-10 items-center rounded border border-silver-darker bg-silver-light px-4 py-2 font-semibold text-secondary">
            {composedUrl}
          </div>
        </FormControl>
      )}

      <FormControl>
        <FormLabel>Add a service name</FormLabel>
        <FormHelperText>
          This is the name of your service on the GraphOS Router.
        </FormHelperText>
        <Input
          value={values.unregisteredSubgraphName}
          onChange={(e) => {
            setFieldValue('unregisteredSubgraphName', e.target.value);
          }}
          name="unregisteredSubgraphName"
          placeholder="My Private Service Name"
        />
      </FormControl>
      <FormControl>
        <FormLabel>Provide the GraphQL Schema for your service</FormLabel>
        <FormHelperText>
          This is required. You can paste your schema below.
        </FormHelperText>
        {(schemaUploaded ||
          introspectionFailureType === 'introspectionDisabled') && (
          <div className="mb-2 flex h-5 text-sm font-normal">
            {schemaUploaded ? (
              <>
                <IconSuccessSolid className="mr-2 mt-1 text-icon-success" />
                Schema uploaded
              </>
            ) : (
              introspectionFailureType === 'introspectionDisabled' && (
                <>👀 Please upload your schema</>
              )
            )}
          </div>
        )}
        <UploadSchema
          setSchemaUploaded={(value) => {
            setSchemaUploaded(value);
            setIsUsingLocalIntrospection(false);
          }}
          sdl={values.sdl}
          setSdl={(value) => setFieldValue('sdl', value, true)}
        />
      </FormControl>
    </div>
  );
};
